import type {
  UserSettingEntityInput,
  UserSettingEntityLike,
  UserSettingKeyInput,
} from "@/client/ui/hooks/useUserSetting";
import { useUserSetting } from "@/client/ui/hooks/useUserSetting";
import { useUserSettingHistory } from "@/client/ui/hooks/useUserSettingHistory";
import type { UserSettingTextEntity } from "@/data/userSettings";
import { formatRelativeTime } from "@/util/date";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { TextInputInstance, ViewInstance } from "react-native";
import { Pressable, ScrollView } from "react-native";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import { tv } from "tailwind-variants";
import type { FloatingMenuModalMenuProps } from "./FloatingMenuModal";
import { FloatingMenuModal } from "./FloatingMenuModal";
import { ProgressPieIcon } from "./ProgressPieIcon";
import { RectButton } from "./RectButton";
import { ScopedVariables } from "./ScopedVariables";
import { TextInputMulti } from "./TextInputMulti";
import { useUserSettingTextDefaultValue } from "./hooks/useUserSettingTextDefaultValue";
import { TextInputSingle } from "./TextInputSingle";

interface InlineEditableSettingTextProps<T extends UserSettingTextEntity> {
  setting: UserSettingEntityLike<T>;
  settingKey: UserSettingKeyInput<T>;
  readonly?: boolean;
  placeholder: string;
  /**
   * @deprecated Use `useUserSettingTextDefaultValue` hook instead for better
   * consistency and to avoid confusion with the `defaultValue` prop of the
   * underlying `TextInput`.
   */
  defaultValue?: string;
  multiline?: boolean;
  maxLength?: number;
  showCounterAtRatio?: number;
  overLimitMessage?: string;
  counterLength?: (value: string) => number;
  textClassName?: string;
  displayContainerClassName?: string;
  displayHoverClassName?: string;
  renderDisplay?: (value: string) => ReactNode;
  sanitizeValue?: (value: string) => string | null;
  onSaveValue?: (value: string | null) => void;
}

const defaultSanitizeValue = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

export function InlineEditableSettingText<T extends UserSettingTextEntity>({
  setting,
  settingKey,
  readonly = false,
  placeholder,
  // oxlint-disable-next-line typescript/no-deprecated
  defaultValue,
  multiline = false,
  maxLength,
  showCounterAtRatio = 0.8,
  overLimitMessage,
  counterLength,
  textClassName = `pyly-body`,
  displayContainerClassName,
  displayHoverClassName,
  renderDisplay,
  sanitizeValue = defaultSanitizeValue,
  onSaveValue,
}: InlineEditableSettingTextProps<T>): ReactNode {
  "use memo";
  const { value, setValue } = useUserSetting({ setting, key: settingKey });
  const history = useUserSettingHistory(setting, settingKey);
  const defaultValueFromHook = useUserSettingTextDefaultValue(
    setting,
    settingKey,
  );
  const currentValue: string = value?.text ?? ``;
  const fallbackValue = defaultValue ?? defaultValueFromHook ?? ``;
  const displayValue = currentValue.length > 0 ? currentValue : fallbackValue;
  const hasDisplayValue = displayValue.length > 0;
  const sanitizedDefaultValue = sanitizeValue(fallbackValue);

  const [isEditing, setIsEditing] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [draft, setDraft] = useState(displayValue);
  const containerRef = useRef<ViewInstance>(null);
  const inputRef = useRef<TextInputInstance>(null);

  if (!isEditing && draft !== displayValue) {
    setDraft(displayValue);
    setIsInputFocused(false);
  }

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const exitEditMode = () => {
      saveDraftValue(
        draft,
        currentValue,
        sanitizeValue,
        sanitizedDefaultValue,
        setValue,
        onSaveValue,
      );
      setIsEditing(false);
    };

    const isWithinModal = (element: Node): boolean => {
      let current = element as HTMLElement | null;
      while (current) {
        // Check for modal indicators
        // React Native Modal renders with role="dialog" on web
        if (current.getAttribute(`role`) === `dialog`) {
          return true;
        }
        // Also check for React Native Web's modal container
        // which typically has very high z-index (9999+)
        const zIndex = window.getComputedStyle(current).zIndex;
        if (zIndex && Math.trunc(Number(zIndex)) >= 9999) {
          return true;
        }
        current = current.parentElement;
      }
      return false;
    };

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const container = containerRef.current as unknown as Node | null;

      // Don't exit edit mode if clicking within a modal (different "layer")
      if (isWithinModal(target)) {
        return;
      }

      if (container && !container.contains(target)) {
        exitEditMode();
      }
    };

    // Add slight delay to avoid triggering on the same click that opened edit mode
    const timeoutId = setTimeout(() => {
      document.addEventListener(`mousedown`, handleClickOutside);
      document.addEventListener(`touchstart`, handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener(`mousedown`, handleClickOutside);
      document.removeEventListener(`touchstart`, handleClickOutside);
    };
  }, [
    currentValue,
    draft,
    isEditing,
    onSaveValue,
    sanitizeValue,
    sanitizedDefaultValue,
    setValue,
  ]);

  const handleKeyPress = (event: {
    nativeEvent: { key: string; shiftKey?: boolean };
    preventDefault: () => void;
  }) => {
    const isShiftPressed = event.nativeEvent.shiftKey === true;

    if (event.nativeEvent.key === `Enter`) {
      if (multiline && isShiftPressed) {
        return;
      }
      event.preventDefault();
      setIsEditing(false);
      saveDraftValue(
        draft,
        currentValue,
        sanitizeValue,
        sanitizedDefaultValue,
        setValue,
        onSaveValue,
      );
    }

    if (event.nativeEvent.key === `Escape`) {
      event.preventDefault();
      setDraft(currentValue);
      setIsEditing(false);
    }
  };

  const displayText = hasDisplayValue ? displayValue : placeholder;
  const displayContent = hasDisplayValue
    ? (renderDisplay?.(displayText) ?? displayText)
    : displayText;
  const historyEntries = getHistoryEntries(history.entries, currentValue);
  const showHistoryButton =
    isEditing && draft.trim().length === 0 && historyEntries.length > 0;

  const displayViewClassName = displayContainerClass({
    class: [displayContainerClassName, displayHoverClassName],
  });
  const editContainerClassName = editContainerClass();
  const displayTextClassName = displayTextClass({
    class: textClassName,
  });
  const emptyTextClassName = tv({})({
    class: [textClassName, `select-none text-fg/20`],
  });
  const inputTextClassName = inputTextClass({
    class: textClassName,
  });
  const counterThreshold =
    maxLength == null ? null : Math.ceil(maxLength * showCounterAtRatio);
  const currentLength = counterLength?.(draft) ?? draft.length;
  const showCounter =
    maxLength != null &&
    counterThreshold != null &&
    currentLength >= counterThreshold;
  const isAtLimit = maxLength != null && currentLength >= maxLength;
  const isTooLong = maxLength != null && currentLength > maxLength;

  if (readonly) {
    if (!hasDisplayValue) {
      return null;
    }

    return (
      <View>
        <View className={displayViewClassName}>
          <Text className={displayTextClassName}>{displayContent}</Text>
        </View>
      </View>
    );
  }

  return (
    <View>
      {isEditing ? (
        <View
          ref={containerRef}
          className={[
            `relative rounded-md bg-bg-high outline-2 -outline-offset-2`,
            isInputFocused ? `outline-blue` : `outline-transparent`,
            editContainerClassName,
          ].join(` `)}
        >
          {multiline ? (
            <TextInputMulti
              ref={inputRef}
              autoFocus
              autoResizeMinHeight={34}
              value={draft}
              onChangeText={setDraft}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                setIsInputFocused(true);
              }}
              onBlur={() => {
                setIsInputFocused(false);
              }}
              placeholder={placeholder}
              className={inputTextClassName}
              variant="bare"
              style={showHistoryButton ? { paddingRight: 32 } : undefined}
            />
          ) : (
            <TextInputSingle
              ref={inputRef}
              autoFocus
              value={draft}
              onChangeText={setDraft}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                setIsInputFocused(true);
              }}
              onBlur={() => {
                setIsInputFocused(false);
              }}
              placeholder={placeholder}
              className={inputTextClassName}
              variant="bare"
              style={showHistoryButton ? { paddingRight: 32 } : undefined}
            />
          )}
          {showCounter ? (
            <View className="mt-1 flex-row items-center justify-between gap-2">
              {overLimitMessage == null ? (
                <View />
              ) : (
                <Text
                  className={
                    isTooLong
                      ? `font-sans text-[12px] text-fg`
                      : `font-sans text-[12px] text-muted-fg`
                  }
                  style={
                    isTooLong ? { color: `var(--color-warning)` } : undefined
                  }
                >
                  {overLimitMessage}
                </Text>
              )}
              <View className="flex-row items-center gap-1">
                <ScopedVariables
                  variables={{
                    "--color-fg": isAtLimit
                      ? `var(--color-warning)`
                      : `var(--color-muted-fg)`,
                  }}
                >
                  <Text className="text-right text-[11px] text-fg">
                    {currentLength}/{maxLength}
                  </Text>
                </ScopedVariables>
                <ProgressPieIcon
                  progress={maxLength == 0 ? 0 : currentLength / maxLength}
                  warn={isAtLimit}
                  size={12}
                />
              </View>
            </View>
          ) : null}
          {showHistoryButton ? (
            <View className="absolute top-2 right-2">
              <FloatingMenuModal
                menu={
                  <InlineEditableSettingHistoryMenu
                    entries={historyEntries}
                    onSelect={(nextValue) => {
                      setDraft(nextValue);
                      inputRef.current?.focus();
                    }}
                  />
                }
              >
                <RectButton
                  variant="bare"
                  iconStart="time-circled"
                  iconSize={16}
                  className={`text-muted-fg`}
                />
              </FloatingMenuModal>
            </View>
          ) : null}
        </View>
      ) : (
        <Pressable
          accessibilityRole="button"
          className={pressableClass()}
          onPress={() => {
            setIsEditing(true);
          }}
        >
          <View className={displayViewClassName}>
            <Text
              className={
                hasDisplayValue ? displayTextClassName : emptyTextClassName
              }
            >
              {displayContent}
            </Text>
          </View>
        </Pressable>
      )}
    </View>
  );
}

function saveDraftValue<T extends UserSettingTextEntity>(
  draft: string,
  currentValue: string,
  sanitizeValue: (value: string) => string | null,
  sanitizedDefaultValue: string | null,
  setValue: (value: UserSettingEntityInput<T> | null) => void,
  onSaveValue?: (value: string | null) => void,
) {
  const sanitized = sanitizeValue(draft);
  const sanitizedCurrentValue = sanitizeValue(currentValue);
  const isUnchanged = sanitized === sanitizedCurrentValue;

  if (isUnchanged && currentValue.length > 0) {
    return;
  }

  const isDefaultValue =
    sanitized != null && sanitizedDefaultValue != null
      ? sanitized === sanitizedDefaultValue
      : sanitized == null && sanitizedDefaultValue == null;
  const nextValue =
    sanitized == null || isDefaultValue
      ? null
      : ({ text: sanitized } as UserSettingEntityInput<T>);

  setValue(nextValue);
  onSaveValue?.(nextValue?.text ?? null);
}

const pressableClass = tv({
  base: `
    group

    focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg/60

    web:transition-colors
  `,
});

const displayContainerClass = tv({
  base: `
    -mx-2 rounded-md px-2 py-1

    group-hover:bg-fg-bg10
  `,
});

const editContainerClass = tv({
  base: `-mx-2 rounded-md px-2 py-1`,
});

const displayTextClass = tv({
  base: `text-fg`,
});

const inputTextClass = tv({
  base: `max-h-80 text-fg`,
});

type HistoryEntry = {
  id: string;
  value: string;
  createdAt: Date;
};

function getHistoryEntries(
  entries: readonly { id: string; createdAt: Date; value: unknown }[],
  currentValue: string,
): HistoryEntry[] {
  const seenValues = new Set<string>();
  const result: HistoryEntry[] = [];

  for (const entry of entries) {
    const nextValue = (entry.value as { text?: string } | null)?.text ?? null;
    if (nextValue == null) {
      continue;
    }
    const trimmed = nextValue.trim();
    if (trimmed.length === 0) {
      continue;
    }
    if (trimmed === currentValue) {
      continue;
    }
    if (seenValues.has(trimmed)) {
      continue;
    }
    seenValues.add(trimmed);
    result.push({ id: entry.id, value: trimmed, createdAt: entry.createdAt });
  }

  return result;
}

function InlineEditableSettingHistoryMenu({
  entries,
  onSelect,
  onRequestClose,
}: {
  entries: HistoryEntry[];
  onSelect: (value: string) => void;
} & FloatingMenuModalMenuProps) {
  return (
    <ScrollView
      className="max-h-100 w-75 rounded-xl bg-bg-high shadow-lg"
      contentContainerClassName="gap-1 p-2"
    >
      {entries.map((entry) => (
        <Pressable
          key={entry.id}
          onPress={() => {
            onSelect(entry.value);
            onRequestClose?.();
          }}
        >
          <View
            className={`
              gap-1 rounded-lg px-3 py-2

              hover:bg-fg-bg10
            `}
          >
            <Text className="font-sans text-[14px] text-fg">{entry.value}</Text>
            <Text className="font-sans text-[12px] text-muted-fg">
              {formatRelativeTime(entry.createdAt)}
            </Text>
          </View>
        </Pressable>
      ))}
    </ScrollView>
  );
}
